import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Key,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { url } from '../constants';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
  const [countdown, setCountdown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    otp: '',
    newPassword: '',
    confirmPassword: '',
  });

  // OTP Resend Timer
  useEffect(() => {
    let interval;
    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown((c) => c - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [countdown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
    if (apiError) {
      setApiError('');
    }
  };

  const validateEmail = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateOTP = () => {
    const newErrors = {};
    if (!formData.otp.trim()) {
      newErrors.otp = 'OTP is required';
    } else if (formData.otp.length !== 6) {
      newErrors.otp = 'Enter valid 6-digit OTP';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePassword = () => {
    const newErrors = {};
    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirm password is required';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sendOTP = async () => {
    if (!validateEmail()) return;
    
    setLoading(true);
    setApiError('');
    try {
      const response = await fetch(`${url}/users/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email.trim().toLowerCase() }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send OTP');
      }

      setSuccessMessage('OTP sent to your email! Check inbox/spam.');
      setCountdown(60); // 60 seconds timer
      setStep(2);
    } catch (error) {
      setApiError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!validateOTP()) return;

    setLoading(true);
    setApiError('');
    try {
      const response = await fetch(`${url}/users/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: formData.email.trim().toLowerCase(), 
          otp: formData.otp 
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Invalid OTP');
      }

      setSuccessMessage('OTP verified! Set your new password.');
      setStep(3);
    } catch (error) {
      setApiError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async () => {
    if (!validatePassword()) return;

    setLoading(true);
    setApiError('');
    try {
      const response = await fetch(`${url}/users/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: formData.email.trim().toLowerCase(), 
          otp: formData.otp,
          newPassword: formData.newPassword 
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Reset failed');
      }

      setSuccessMessage('Password reset successfully! Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      setApiError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = () => {
    setCountdown(60);
    sendOTP();
  };

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          py: { xs: 4, md: 8 },
          background: `
            linear-gradient(135deg, #0f5328 0%, #1a7f3a 25%, #2d9e4a 50%, #4caf50 75%, #66bb6a 100%),
            radial-gradient(circle at 15% 25%, rgba(255,255,255,0.25) 0%, transparent 50%),
            radial-gradient(circle at 85% 65%, rgba(255,255,255,0.2) 0%, transparent 50%),
            radial-gradient(ellipse at 35% 15%, rgba(76,175,80,0.3) 0%, transparent 60%),
            radial-gradient(circle at 65% 85%, rgba(26,127,58,0.25) 0%, transparent 50%)
          `,
          backgroundSize: '400% 400%, 250px 250px, 350px 350px, 300px 200px, 220px 220px',
          animation: 'heroShift 6s ease infinite',
          overflow: 'hidden',
        }}
      >
        {/* Same 5 Floating Shapes */}
        <Box sx={{ position: 'absolute', top: '10%', left: '5%', width: 200, height: 200, background: 'radial-gradient(circle, rgba(76,175,80,0.5) 0%, rgba(26,127,58,0.2) 70%)', borderRadius: '50%', animation: 'float1 18s infinite linear', boxShadow: '0 0 40px rgba(76,175,80,0.6)', zIndex: 1 }} />
        <Box sx={{ position: 'absolute', top: '65%', right: '8%', width: 160, height: 160, background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 70%)', borderRadius: '50%', animation: 'float2 14s infinite linear', boxShadow: '0 0 30px rgba(255,255,255,0.5)', zIndex: 1 }} />
        <Box sx={{ position: 'absolute', top: '25%', right: '15%', width: 140, height: 140, background: 'linear-gradient(45deg, rgba(46,158,74,0.45) 0%, rgba(102,187,106,0.3) 100%)', clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)', animation: 'float3 16s infinite linear', boxShadow: '0 0 25px rgba(46,158,74,0.5)', zIndex: 1 }} />
        <Box sx={{ position: 'absolute', bottom: '20%', left: '20%', width: 100, height: 100, background: 'radial-gradient(circle, rgba(255,255,255,0.45) 0%, rgba(76,175,80,0.4) 70%)', borderRadius: '50%', animation: 'float4 12s infinite linear', boxShadow: '0 0 20px rgba(76,175,80,0.6)', zIndex: 1 }} />
        <Box sx={{ position: 'absolute', top: '45%', right: '25%', width: 180, height: 80, background: 'linear-gradient(90deg, rgba(26,127,58,0.4) 0%, rgba(46,158,74,0.5) 50%, rgba(102,187,106,0.3) 100%)', borderRadius: '50px', animation: 'float5 22s infinite linear', boxShadow: '0 0 35px rgba(26,127,58,0.5)', zIndex: 1 }} />

        <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center', width: '100%', position: 'relative', zIndex: 10, py: 4 }}>
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 4, sm: 5 }, 
              borderRadius: 3, 
              width: '100%',
              maxWidth: 450,
              bgcolor: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          >
            {/* Back Button */}
            {step > 1 && (
              <Button
                startIcon={<ArrowBack />}
                onClick={() => setStep(step - 1)}
                sx={{ mb: 2, color: '#1a7f3a', textTransform: 'none' }}
              >
                Back
              </Button>
            )}

            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" gutterBottom fontWeight={700} sx={{ color: '#1a7f3a' }}>
                {step === 1 ? 'Forgot Password?' : step === 2 ? 'Verify OTP' : 'Reset Password'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {step === 1 && "Enter your email to receive OTP"}
                {step === 2 && "Enter the 6-digit code sent to your email"}
                {step === 3 && "Create a new password for your account"}
              </Typography>
            </Box>

            {/* Success/Error Messages */}
            {successMessage && (
              <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
                {successMessage}
              </Alert>
            )}
            {apiError && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setApiError('')}>
                {apiError}
              </Alert>
            )}

            {/* Step 1: Email */}
            {step === 1 && (
              <Stack spacing={3} component="form">
                <TextField
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  placeholder="your.email@example.com"
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Email color="action" /></InputAdornment>,
                  }}
                  disabled={loading}
                />
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={sendOTP}
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    fontWeight: 600,
                    bgcolor: '#1a7f3a',
                    '&:hover': { bgcolor: '#0f5328' },
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                      Sending OTP...
                    </>
                  ) : (
                    'Send OTP'
                  )}
                </Button>
              </Stack>
            )}

            {/* Step 2: OTP */}
            {step === 2 && (
              <Stack spacing={3} component="form">
                <TextField
                  label="Enter OTP"
                  name="otp"
                  value={formData.otp}
                  onChange={handleChange}
                  error={!!errors.otp}
                  helperText={errors.otp || '6-digit code'}
                  placeholder="123456"
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Key color="action" /></InputAdornment>,
                  }}
                  disabled={loading}
                />
                
                {/* Resend OTP */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {countdown > 0 ? `Resend OTP in ${countdown}s` : 'Didn\'t receive OTP?'}
                  </Typography>
                  <Button
                    variant="text"
                    onClick={resendOTP}
                    disabled={countdown > 0 || loading}
                    sx={{ color: '#1a7f3a', textTransform: 'none', mt: 0.5 }}
                  >
                    Resend OTP
                  </Button>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={verifyOTP}
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    fontWeight: 600,
                    bgcolor: '#1a7f3a',
                    '&:hover': { bgcolor: '#0f5328' },
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                      Verifying...
                    </>
                  ) : (
                    'Verify OTP'
                  )}
                </Button>
              </Stack>
            )}

            {/* Step 3: New Password */}
            {step === 3 && (
              <Stack spacing={3} component="form">
                <TextField
                  label="New Password"
                  name="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.newPassword}
                  onChange={handleChange}
                  error={!!errors.newPassword}
                  helperText={errors.newPassword}
                  placeholder="At least 6 characters"
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  disabled={loading}
                />
                <TextField
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  placeholder="Re-enter new password"
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  disabled={loading}
                />
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={resetPassword}
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    fontWeight: 600,
                    bgcolor: '#1a7f3a',
                    '&:hover': { bgcolor: '#0f5328' },
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                      Resetting Password...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </Stack>
            )}

            {/* Back to Login */}
            <Divider sx={{ my: 3 }} />
            <Button
              fullWidth
              variant="text"
              onClick={() => navigate('/login')}
              sx={{ color: '#1a7f3a', fontWeight: 500 }}
            >
              Back to Login
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* Same Beautiful Animations */}
      <style jsx>{`
        @keyframes heroShift {
          0%, 100% { background-position: 0% 50%, 20% 30%, 85% 65%, 35% 15%, 65% 85%; }
          25% { background-position: 100% 50%, 100% 100%, 15% 100%, 85% 50%, 35% 0%; }
          50% { background-position: 50% 100%, 0% 0%, 100% 100%, 0% 50%, 100% 50%; }
          75% { background-position: 0% 50%, 0% 100%, 85% 0%, 35% 100%, 65% 50%; }
        }
        @keyframes float1 { 0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); } 25% { transform: translate(40px, -40px) rotate(90deg) scale(1.1); } 50% { transform: translate(-20px, 30px) rotate(180deg) scale(0.95); } 75% { transform: translate(25px, -25px) rotate(270deg) scale(1.05); } }
        @keyframes float2 { 0%, 100% { transform: translate(0, 0) rotate(0deg); } 33% { transform: translate(-50px, 40px) rotate(-120deg); } 66% { transform: translate(35px, -35px) rotate(-240deg); } }
        @keyframes float3 { 0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); } 33% { transform: translate(-30px, 25px) rotate(120deg) scale(1.15); } 66% { transform: translate(20px, -20px) rotate(240deg) scale(0.9); } }
        @keyframes float4 { 0%, 100% { transform: translate(0, 0) rotate(0deg); } 50% { transform: translate(-25px, 25px) rotate(180deg); } }
        @keyframes float5 { 0%, 100% { transform: translate(0, 0) scale(1); } 33% { transform: translate(30px, -20px) scale(1.1); } 66% { transform: translate(-25px, 20px) scale(0.95); } }
      `}</style>
    </>
  );
};

export default ForgotPassword;
